import React from "react";
import {
  AbsoluteFill,
  Audio,
  interpolate,
  spring,
  staticFile,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";
import {
  ArrowRight,
  Briefcase,
  CheckCircle2,
  Cpu,
  Flame,
  FolderGit2,
  Layers,
  Pin,
  Sparkles,
  TrendingUp,
  Workflow,
  Zap,
} from "lucide-react";
import { DynamicSubtitle, SubtitleItem } from "./components/DynamicSubtitle";
import {
  TeenageEngineeringBot,
  OPZuraPose,
} from "./components/AntiSlopHardwareMascots";

// 16 Exact Subtitle Segments aligned with Zura's actual voice recording
const SUBTITLES: SubtitleItem[] = [
  {
    from: 0,
    to: 125,
    text: "Yang paling bikin capek pas bangun usaha sendirian itu bukan tenaganya,",
    highlight: "bukan tenaganya",
  },
  {
    from: 125,
    to: 220,
    text: "tapi otak yang dipaksa jadi dua orang berbeda.",
    highlight: "otak dipaksa jadi dua orang",
  },
  {
    from: 220,
    to: 330,
    text: "Di satu jam lo harus liar mikirin ide marketing biar gak tenggelam,",
    highlight: "liar mikirin ide marketing",
  },
  {
    from: 330,
    to: 500,
    text: "tapi di jam berikutnya lo dipaksa super rapi ngerancang alur sistem biar bisnis gak berantakan.",
    highlight: "super rapi ngerancang alur sistem",
  },
  {
    from: 500,
    to: 650,
    text: "Tiap hari gonta-ganti peran sendirian kayak gitu bikin cepet burnout dan mentok ide.",
    highlight: "cepet burnout dan mentok ide",
  },
  {
    from: 650,
    to: 785,
    text: "Titik baliknya berubah pas gue mulai bangun tim AI agent otonom di laptop.",
    highlight: "tim AI agent otonom di laptop",
  },
  {
    from: 785,
    to: 885,
    text: "Rasanya beda banget sama cuma sekadar nanya-nanya ke ChatGPT.",
    highlight: "beda banget sama nanya ChatGPT",
  },
  {
    from: 885,
    to: 955,
    text: "Di sini gue beneran ngerasa punya tim:",
    highlight: "beneran ngerasa punya tim",
  },
  {
    from: 955,
    to: 1075,
    text: "Gue lempar satu target bisnis, agen-agennya langsung bagi tugas sendiri.",
    highlight: "langsung bagi tugas sendiri",
  },
  {
    from: 1075,
    to: 1135,
    text: "Yang satu ngeriset sudut pandang marketing,",
    highlight: "ngeriset sudut pandang marketing",
  },
  {
    from: 1135,
    to: 1255,
    text: "yang satu nyusun struktur sistemnya sampai rapi dan mereka kerja barengan,",
    highlight: "nyusun struktur sistem sampai rapi",
  },
  {
    from: 1255,
    to: 1315,
    text: "tanpa gue harus nuntun tiap detik.",
    highlight: "tanpa harus nuntun tiap detik",
  },
  {
    from: 1315,
    to: 1410,
    text: "Lo gak lagi ngerasa kesepian mikir semuanya sendirian,",
    highlight: "gak lagi ngerasa kesepian",
  },
  {
    from: 1410,
    to: 1505,
    text: "tapi lo beneran punya divisi kerja nyata di belakang layar.",
    highlight: "punya divisi kerja nyata",
  },
  {
    from: 1505,
    to: 1600,
    text: "Kalau lo lagi di fase capek yang sama, follow @zura.ailab",
    highlight: "follow @zura.ailab",
  },
  {
    from: 1600,
    to: 1670,
    text: "dan pantau cara gue ngejalanin sistem ini.",
    highlight: "pantau cara ngejalanin sistem",
  },
];

// FONT DECLARATIONS VIA STATICFILE
const fontFaces = `
@font-face {
  font-family: 'TekoDisplay';
  src: url('${staticFile("fonts/Teko-700.woff2")}') format('woff2');
  font-weight: 700;
  font-style: normal;
}
@font-face {
  font-family: 'BebasDisplay';
  src: url('${staticFile("fonts/BebasNeue-400.woff2")}') format('woff2');
  font-weight: 400;
  font-style: normal;
}
@font-face {
  font-family: 'AntonDisplay';
  src: url('${staticFile("fonts/Anton-400.woff2")}') format('woff2');
  font-weight: 400;
  font-style: normal;
}
@font-face {
  font-family: 'RajdhaniDisplay';
  src: url('${staticFile("fonts/Rajdhani-700.woff2")}') format('woff2');
  font-weight: 700;
  font-style: normal;
}
@font-face {
  font-family: 'ChakraDisplay';
  src: url('${staticFile("fonts/ChakraPetch-700.woff2")}') format('woff2');
  font-weight: 700;
  font-style: normal;
}
`;

export const ZuraSolopreneurTeam: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // Snappy spring helper
  const sp = (delay: number, damping = 16, stiffness = 130) =>
    spring({
      frame: Math.max(0, frame - delay),
      fps,
      config: { damping, stiffness },
    });

  // Smooth scene opacities
  const s1Exit = interpolate(frame, [205, 225], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  const s1Opacity = 1 - s1Exit;
  const s1Y = interpolate(s1Exit, [0, 1], [0, -32]);

  const s2Exit = interpolate(frame, [635, 655], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  const s2Enter = interpolate(frame, [215, 235], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  const s2Opacity = s2Enter * (1 - s2Exit);
  const s2Y = interpolate(s2Exit, [0, 1], [0, -32]);

  const s3Exit = interpolate(frame, [940, 960], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  const s3Enter = interpolate(frame, [645, 665], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  const s3Opacity = s3Enter * (1 - s3Exit);
  const s3Y = interpolate(s3Exit, [0, 1], [0, -32]);

  const s4Exit = interpolate(frame, [1300, 1320], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  const s4Enter = interpolate(frame, [950, 970], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  const s4Opacity = s4Enter * (1 - s4Exit);
  const s4Y = interpolate(s4Exit, [0, 1], [0, -32]);

  const s5Enter = interpolate(frame, [1310, 1330], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  const s5Opacity = s5Enter;

  // DYNAMIC PRESENTER POSE OF OP-ZURA (REACTS TO VO TIMELINE)
  const getPresenterPose = (): OPZuraPose => {
    if (frame < 125) return "talking"; // Hook opening: talking
    if (frame < 225) return "thinking"; // Otak dipaksa jadi 2 orang: thinking
    if (frame < 360) return "pointing"; // Mode marketing: pointing
    if (frame < 500) return "talking"; // Mode arsitek sistem: talking
    if (frame < 650) return "thinking"; // Burnout & mentok ide: thinking
    if (frame < 785) return "talking"; // Titik balik bangun AI Agent: talking
    if (frame < 950) return "pointing"; // Punya tim sendiri: pointing
    if (frame < 1300) return "typing"; // Delegasi & coding bareng: typing
    return "coffee"; // Outro: Chillin with {Z} coffee mug
  };

  const currentPose = getPresenterPose();

  // KINEMATIC FLIGHT & ROVING PATH FOR OP-ZURA (FLOATING LEFT & RIGHT)
  const flightKeyframes = [
    { frame: 0, x: 0, y: 320, bank: 0 },
    { frame: 60, x: -130, y: 335, bank: -5 },
    { frame: 130, x: 110, y: 325, bank: 4 },
    { frame: 210, x: 0, y: 315, bank: -1 },
    // Scene 2: Jam 10 Marketing -> Swoops to the LEFT above marketing card
    { frame: 250, x: -230, y: 360, bank: -7 },
    { frame: 330, x: -245, y: 350, bank: -3 },
    // Scene 2: Jam 11 System -> Glides across to the RIGHT above system card
    { frame: 370, x: 0, y: 320, bank: 5 },
    { frame: 430, x: 230, y: 365, bank: 7 },
    { frame: 490, x: 245, y: 355, bank: 3 },
    // Scene 2: Burnout -> Returns to center with worried hover
    { frame: 540, x: 80, y: 325, bank: -3 },
    { frame: 620, x: 0, y: 310, bank: 0 },
    // Scene 3: Turning Point AI Agent -> Swoops left then right excitedly
    { frame: 680, x: -170, y: 350, bank: -6 },
    { frame: 780, x: 0, y: 310, bank: 2 },
    { frame: 860, x: 190, y: 345, bank: 6 },
    { frame: 940, x: 0, y: 315, bank: -1 },
    // Scene 4: Agent 1 & Agent 2 -> Patrols between both agents!
    { frame: 990, x: -220, y: 365, bank: -7 }, // Inspecting Agent 1
    { frame: 1080, x: -200, y: 355, bank: -2 },
    { frame: 1140, x: 0, y: 320, bank: 5 }, // Crossing over
    { frame: 1200, x: 220, y: 365, bank: 7 }, // Inspecting Agent 2
    { frame: 1270, x: 190, y: 350, bank: 2 },
    // Scene 5: Outro -> Graceful glide to center for coffee
    { frame: 1320, x: -130, y: 335, bank: -4 },
    { frame: 1420, x: 0, y: 310, bank: 0 },
    { frame: 1670, x: 0, y: 310, bank: 0 },
  ];

  const kfFrames = flightKeyframes.map((k) => k.frame);
  const kfXs = flightKeyframes.map((k) => k.x);
  const kfYs = flightKeyframes.map((k) => k.y);
  const kfBanks = flightKeyframes.map((k) => k.bank);

  const baseX = interpolate(frame, kfFrames, kfXs, {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  const baseY = interpolate(frame, kfFrames, kfYs, {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  const baseBank = interpolate(frame, kfFrames, kfBanks, {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  // Continuous micro anti-gravity float physics
  const microFloatY = Math.sin(frame / 16) * 9;
  const microFloatX = Math.cos(frame / 20) * 7;
  const microFloatRot = Math.sin(frame / 22) * 2;

  const totalMascotX = baseX + microFloatX;
  const totalMascotY = baseY + microFloatY;
  const totalMascotRot = baseBank + microFloatRot;

  return (
    <AbsoluteFill
      style={{
        backgroundColor: "#F4F1EA",
        color: "#1C1917",
        fontFamily: "'Inter', sans-serif",
        overflow: "hidden",
      }}
    >
      <style>{fontFaces}</style>

      {/* AUDIO TRACKS: Voiceover + Ambient Clean BGM Ducked */}
      <Audio src={staticFile("vo_zura_solopreneur.mp3")} volume={1.0} />
      <Audio src={staticFile("bgm-clean.mp3")} volume={0.08} loop />

      {/* SFX ACCENTS */}
      {frame >= 0 && frame < 30 && <Audio src={staticFile("ui-pop.mp3")} volume={0.35} />}
      {frame >= 220 && frame < 250 && <Audio src={staticFile("sfx_card_flip.mp3")} volume={0.4} />}
      {frame >= 335 && frame < 365 && <Audio src={staticFile("click-soft.mp3")} volume={0.35} />}
      {frame >= 505 && frame < 535 && <Audio src={staticFile("whoosh.mp3")} volume={0.4} />}
      {frame >= 650 && frame < 680 && <Audio src={staticFile("ui-success.mp3")} volume={0.4} />}
      {frame >= 955 && frame < 985 && <Audio src={staticFile("pop.mp3")} volume={0.35} />}
      {frame >= 1315 && frame < 1345 && <Audio src={staticFile("sfx_card_flip.mp3")} volume={0.4} />}
      {frame >= 1500 && frame < 1530 && <Audio src={staticFile("ui-pop.mp3")} volume={0.35} />}

      {/* TACTILE PAPER GRAIN & FINE PIN-BOARD DOT GRID */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage:
            "radial-gradient(circle, rgba(28, 25, 23, 0.08) 1.5px, transparent 1.5px)",
          backgroundSize: "32px 32px",
          pointerEvents: "none",
        }}
      />

      {/* SUBTLE PAPER VIGNETTE BORDER */}
      <div
        style={{
          position: "absolute",
          inset: 24,
          border: "2px dashed rgba(28, 25, 23, 0.14)",
          borderRadius: 36,
          pointerEvents: "none",
        }}
      />

      {/* TOP HEADER BRAND CAPSULE (SAFE ZONE Y: 260px) */}
      <div
        style={{
          position: "absolute",
          top: 260,
          left: 0,
          right: 0,
          display: "flex",
          justifyContent: "center",
          zIndex: 80,
          transform: `translateY(${interpolate(sp(0, 18, 120), [0, 1], [-20, 0])}px)`,
          opacity: sp(0, 18, 120),
        }}
      >
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 12,
            backgroundColor: "#FFFFFF",
            padding: "10px 24px",
            borderRadius: 999,
            border: "2.5px solid #1C1917",
            boxShadow: "4px 4px 0px #1C1917",
          }}
        >
          <Briefcase size={20} color="#EA580C" strokeWidth={2.5} />
          <span
            style={{
              fontFamily: "'RajdhaniDisplay', sans-serif",
              fontSize: 20,
              fontWeight: 700,
              letterSpacing: 2,
              color: "#1C1917",
              textTransform: "uppercase",
            }}
          >
            SOLOPRENEUR REALITY CHECK · @ZURA.AILAB
          </span>
          <div
            style={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              backgroundColor: "#EA580C",
            }}
          />
        </div>
      </div>

      {/* ========================================================= */}
      {/* PERSISTENT KINEMATIC PRESENTER: OP-ZURA (THE FLYING HOST)*/}
      {/* Roams smoothly left and right, banking into movement      */}
      {/* ========================================================= */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          display: "flex",
          justifyContent: "center",
          zIndex: 70,
          pointerEvents: "none",
          transform: `translate(${totalMascotX}px, ${totalMascotY}px) rotate(${totalMascotRot}deg)`,
          transformOrigin: "center center",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            position: "relative",
          }}
        >
          {/* Subtle anti-gravity propulsion hover shadow */}
          <div
            style={{
              position: "absolute",
              bottom: 12,
              width: 140,
              height: 18,
              borderRadius: "50%",
              backgroundColor: "rgba(28, 25, 23, 0.12)",
              filter: "blur(6px)",
              transform: `scale(${1 + Math.sin(frame / 16) * 0.15})`,
            }}
          />

          <TeenageEngineeringBot pose={currentPose} size={250} floating={false} />
        </div>
      </div>

      {/* ========================================================= */}
      {/* SUPPORTING STAGE CARDS (Y: 640px - 1410px, WIDTH: 840px) */}
      {/* Shows the visual topic that OP-ZURA is narrating about   */}
      {/* ========================================================= */}
      <div
        style={{
          position: "absolute",
          top: 640,
          bottom: 510,
          left: 0,
          right: 0,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          zIndex: 50,
        }}
      >
        <div
          style={{
            width: 840,
            height: "100%",
            position: "relative",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }}
        >
          {/* ========================================================= */}
          {/* SCENE 1: THE DUAL BRAIN DILEMMA (0 - 225)                 */}
          {/* ========================================================= */}
          {frame < 228 && (
            <div
              style={{
                opacity: s1Opacity,
                transform: `translateY(${s1Y}px)`,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 16,
                width: "100%",
              }}
            >
              {/* Headline */}
              <div style={{ textAlign: "center", width: "100%" }}>
                <div
                  style={{
                    fontFamily: "'AntonDisplay', sans-serif",
                    fontSize: 68,
                    lineHeight: 0.95,
                    letterSpacing: 1.5,
                    color: "#1C1917",
                    textTransform: "uppercase",
                    transform: `translateY(${interpolate(sp(2, 18, 120), [0, 1], [30, 0])}px)`,
                  }}
                >
                  BANGUN BISNIS SENDIRIAN?
                </div>
                <div
                  style={{
                    fontFamily: "'RajdhaniDisplay', sans-serif",
                    fontSize: 28,
                    fontWeight: 700,
                    letterSpacing: 2,
                    color: "#DC2626",
                    textTransform: "uppercase",
                    marginTop: 6,
                  }}
                >
                  BUKAN CAPEK TENAGA · TAPI OTAK PECAH
                </div>
              </div>

              {/* Two Split Persona Cards */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 18,
                  width: "100%",
                }}
              >
                {/* Persona Left: Creative Brain */}
                <div
                  style={{
                    backgroundColor: "#FFFDF9",
                    border: "3px solid #1C1917",
                    borderRadius: 22,
                    padding: "20px 20px",
                    boxShadow: "6px 6px 0px #1C1917",
                    display: "flex",
                    flexDirection: "column",
                    gap: 10,
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <Sparkles size={24} color="#D97706" />
                      <span style={{ fontFamily: "'BebasDisplay', sans-serif", fontSize: 32, color: "#1C1917" }}>
                        OTAK MARKETING
                      </span>
                    </div>
                    <span style={{ backgroundColor: "#EA580C", color: "#FFFFFF", fontSize: 14, fontWeight: 800, padding: "3px 10px", borderRadius: 6 }}>
                      LIAR
                    </span>
                  </div>
                  <p style={{ fontSize: 18, color: "#4B5563", fontWeight: 600, lineHeight: 1.35, margin: 0 }}>
                    Harus gali ide konten, baca algoritma, & mikirin copywriting.
                  </p>
                </div>

                {/* Persona Right: Structure Brain */}
                <div
                  style={{
                    backgroundColor: "#F8FAFC",
                    border: "3px solid #1C1917",
                    borderRadius: 22,
                    padding: "20px 20px",
                    boxShadow: "6px 6px 0px #1C1917",
                    display: "flex",
                    flexDirection: "column",
                    gap: 10,
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <Cpu size={24} color="#2563EB" />
                      <span style={{ fontFamily: "'BebasDisplay', sans-serif", fontSize: 32, color: "#1C1917" }}>
                        OTAK SISTEM
                      </span>
                    </div>
                    <span style={{ backgroundColor: "#2563EB", color: "#FFFFFF", fontSize: 14, fontWeight: 800, padding: "3px 10px", borderRadius: 6 }}>
                      RAPI
                    </span>
                  </div>
                  <p style={{ fontSize: 18, color: "#4B5563", fontWeight: 600, lineHeight: 1.35, margin: 0 }}>
                    Harus disiplin alur pesanan, database, & otomasi proses.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* ========================================================= */}
          {/* SCENE 2: MARKETING VS SISTEM BURNOUT (215 - 655)          */}
          {/* ========================================================= */}
          {frame >= 212 && frame < 658 && (
            <div
              style={{
                opacity: s2Opacity,
                transform: `translateY(${s2Y}px)`,
                display: "flex",
                flexDirection: "column",
                gap: 16,
                width: "100%",
              }}
            >
              {/* Card 1: Marketing Mode */}
              <div
                style={{
                  backgroundColor: "#FEF9C3",
                  border: "3px solid #1C1917",
                  borderRadius: 20,
                  padding: "18px 24px",
                  boxShadow: "6px 6px 0px #1C1917",
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <Pin size={20} color="#EA580C" />
                    <span style={{ fontFamily: "'RajdhaniDisplay', sans-serif", fontSize: 20, fontWeight: 700, color: "#9A3412" }}>
                      JAM 10:00 — MODE MARKETING LIAR
                    </span>
                  </div>
                  <span style={{ backgroundColor: "#F97316", color: "#FFFFFF", fontSize: 14, fontWeight: 800, padding: "3px 10px", borderRadius: 6 }}>
                    OTAK KANAN
                  </span>
                </div>
                <div style={{ fontFamily: "'BebasDisplay', sans-serif", fontSize: 38, color: "#1C1917", lineHeight: 1.0 }}>
                  MIKIRIN IDE, HOOK KONTEN, & COPYWRITING
                </div>
              </div>

              {/* Card 2: Systems Mode */}
              <div
                style={{
                  backgroundColor: "#EFF6FF",
                  border: "3px solid #1C1917",
                  borderRadius: 20,
                  padding: "18px 24px",
                  boxShadow: "6px 6px 0px #1C1917",
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <Workflow size={20} color="#2563EB" />
                    <span style={{ fontFamily: "'RajdhaniDisplay', sans-serif", fontSize: 20, fontWeight: 700, color: "#1E40AF" }}>
                      JAM 11:00 — MODE ARSITEK SISTEM
                    </span>
                  </div>
                  <span style={{ backgroundColor: "#2563EB", color: "#FFFFFF", fontSize: 14, fontWeight: 800, padding: "3px 10px", borderRadius: 6 }}>
                    OTAK KIRI
                  </span>
                </div>
                <div style={{ fontFamily: "'BebasDisplay', sans-serif", fontSize: 38, color: "#1C1917", lineHeight: 1.0 }}>
                  RANCANG ALUR DATABASE & OTOMASI PROSES
                </div>
              </div>

              {/* Friction Burnout Stamp */}
              <div
                style={{
                  backgroundColor: "#DC2626",
                  color: "#FFFFFF",
                  border: "3px solid #1C1917",
                  borderRadius: 18,
                  padding: "14px 24px",
                  boxShadow: "6px 6px 0px #1C1917",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 12,
                  transform: `rotate(${interpolate(sp(500, 15, 150), [0, 1], [-6, -1])}deg)`,
                }}
              >
                <Flame size={28} color="#FFFFFF" />
                <span style={{ fontFamily: "'AntonDisplay', sans-serif", fontSize: 32, letterSpacing: 1.5 }}>
                  GONTA-GANTI PERAN = CEPET BURNOUT
                </span>
              </div>
            </div>
          )}

          {/* ========================================================= */}
          {/* SCENE 3: THE TURNING POINT — AI AGENT (645 - 960)          */}
          {/* ========================================================= */}
          {frame >= 642 && frame < 962 && (
            <div
              style={{
                opacity: s3Opacity,
                transform: `translateY(${s3Y}px)`,
                display: "flex",
                flexDirection: "column",
                gap: 16,
                width: "100%",
              }}
            >
              <div style={{ textAlign: "center" }}>
                <span style={{ backgroundColor: "#059669", color: "#FFFFFF", fontFamily: "'RajdhaniDisplay', sans-serif", fontSize: 18, fontWeight: 700, letterSpacing: 2, padding: "6px 18px", borderRadius: 999, display: "inline-block", marginBottom: 8 }}>
                  THE TURNING POINT
                </span>
                <div style={{ fontFamily: "'AntonDisplay', sans-serif", fontSize: 60, lineHeight: 0.95, color: "#1C1917", letterSpacing: 1 }}>
                  BANGUN TIM AI AGENT OTONOM
                </div>
              </div>

              {/* The Autonomous Agent Reality */}
              <div
                style={{
                  backgroundColor: "#ECFDF5",
                  border: "3.5px solid #059669",
                  borderRadius: 22,
                  padding: "22px 26px",
                  display: "flex",
                  alignItems: "center",
                  gap: 18,
                  boxShadow: "8px 8px 0px #059669",
                }}
              >
                <div style={{ width: 50, height: 50, borderRadius: 16, backgroundColor: "#A7F3D0", display: "flex", alignItems: "center", justifyContent: "center", border: "2px solid #059669" }}>
                  <CheckCircle2 size={32} color="#059669" />
                </div>
                <div>
                  <div style={{ fontFamily: "'RajdhaniDisplay', sans-serif", fontSize: 24, fontWeight: 700, color: "#065F46", letterSpacing: 2 }}>
                    AUTONOMOUS MULTI-AGENT
                  </div>
                  <div style={{ fontSize: 22, fontWeight: 700, color: "#047857", marginTop: 2 }}>
                    Beneran ngerasa punya tim: Lempar 1 target, mereka bagi tugas sendiri!
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ========================================================= */}
          {/* SCENE 4: PARALLEL AGENT EXECUTION (950 - 1320)             */}
          {/* ========================================================= */}
          {frame >= 948 && frame < 1322 && (
            <div
              style={{
                opacity: s4Opacity,
                transform: `translateY(${s4Y}px)`,
                display: "flex",
                flexDirection: "column",
                gap: 16,
                width: "100%",
              }}
            >
              {/* Mission Input Badge */}
              <div style={{ backgroundColor: "#FFFFFF", border: "3px solid #1C1917", borderRadius: 16, padding: "12px 20px", display: "flex", alignItems: "center", justifyContent: "space-between", boxShadow: "4px 4px 0px #1C1917" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <Layers size={20} color="#2563EB" />
                  <span style={{ fontFamily: "'RajdhaniDisplay', sans-serif", fontSize: 20, fontWeight: 700, letterSpacing: 1.5, color: "#1C1917" }}>
                    TARGET: KEMBANGIN BISNIS & RAPIIN SISTEM
                  </span>
                </div>
                <span style={{ backgroundColor: "#10B981", color: "#FFFFFF", fontSize: 14, fontWeight: 800, padding: "3px 10px", borderRadius: 6 }}>
                  AUTO-DELEGATED
                </span>
              </div>

              {/* Two Parallel Agent Cards */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, width: "100%" }}>
                {/* Agent 01 */}
                <div style={{ backgroundColor: "#FFFDF9", border: "3px solid #1C1917", borderRadius: 20, padding: "18px 18px", boxShadow: "6px 6px 0px #1C1917", display: "flex", flexDirection: "column", gap: 10 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <Sparkles size={20} color="#EA580C" />
                    <span style={{ fontFamily: "'RajdhaniDisplay', sans-serif", fontSize: 19, fontWeight: 700, color: "#1C1917" }}>
                      AGENT 01 · MARKETING
                    </span>
                  </div>
                  <div style={{ fontSize: 16, fontWeight: 600, color: "#374151", lineHeight: 1.3 }}>
                    • Riset angle & perilaku audiens
                    <br />• Susun draf copywriting & penawaran
                  </div>
                </div>

                {/* Agent 02 */}
                <div style={{ backgroundColor: "#F8FAFC", border: "3px solid #1C1917", borderRadius: 20, padding: "18px 18px", boxShadow: "6px 6px 0px #1C1917", display: "flex", flexDirection: "column", gap: 10 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <Workflow size={20} color="#2563EB" />
                    <span style={{ fontFamily: "'RajdhaniDisplay', sans-serif", fontSize: 19, fontWeight: 700, color: "#1C1917" }}>
                      AGENT 02 · SYSTEMS OPS
                    </span>
                  </div>
                  <div style={{ fontSize: 16, fontWeight: 600, color: "#374151", lineHeight: 1.3 }}>
                    • Rancang skema database & API
                    <br />• Otomasi pipeline transaksi server
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ========================================================= */}
          {/* SCENE 5: OUTRO & SYSTEM COMMANDER (1310 - 1670)           */}
          {/* ========================================================= */}
          {frame >= 1305 && (
            <div
              style={{
                opacity: s5Opacity,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 16,
                width: "100%",
              }}
            >
              {/* Title */}
              <div style={{ textAlign: "center" }}>
                <div style={{ fontFamily: "'RajdhaniDisplay', sans-serif", fontSize: 22, fontWeight: 700, letterSpacing: 2, color: "#059669" }}>
                  SOLO FOUNDER ➔ SYSTEM COMMANDER
                </div>
                <div style={{ fontFamily: "'AntonDisplay', sans-serif", fontSize: 62, lineHeight: 0.95, color: "#1C1917", marginTop: 4 }}>
                  PUNYA DIVISI KERJA NYATA
                </div>
              </div>

              {/* 3 Relief Points */}
              <div style={{ display: "flex", justifyContent: "space-between", gap: 14, width: "100%" }}>
                <div style={{ flex: 1, backgroundColor: "#FFFFFF", border: "2.5px solid #1C1917", borderRadius: 16, padding: "12px 10px", textAlign: "center", boxShadow: "4px 4px 0px #1C1917" }}>
                  <Zap size={22} color="#D97706" style={{ margin: "0 auto 4px" }} />
                  <div style={{ fontSize: 16, fontWeight: 700, color: "#1C1917" }}>Nol Kesepian</div>
                </div>
                <div style={{ flex: 1, backgroundColor: "#FFFFFF", border: "2.5px solid #1C1917", borderRadius: 16, padding: "12px 10px", textAlign: "center", boxShadow: "4px 4px 0px #1C1917" }}>
                  <FolderGit2 size={22} color="#2563EB" style={{ margin: "0 auto 4px" }} />
                  <div style={{ fontSize: 16, fontWeight: 700, color: "#1C1917" }}>Sistem Rapi</div>
                </div>
                <div style={{ flex: 1, backgroundColor: "#FFFFFF", border: "2.5px solid #1C1917", borderRadius: 16, padding: "12px 10px", textAlign: "center", boxShadow: "4px 4px 0px #1C1917" }}>
                  <TrendingUp size={22} color="#059669" style={{ margin: "0 auto 4px" }} />
                  <div style={{ fontSize: 16, fontWeight: 700, color: "#1C1917" }}>Fokus Visi</div>
                </div>
              </div>

              {/* Call to Action Box */}
              <div
                style={{
                  backgroundColor: "#FFFFFF",
                  border: "3.5px solid #1C1917",
                  borderRadius: 24,
                  padding: "18px 28px",
                  width: "100%",
                  boxShadow: "8px 8px 0px #1C1917",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  boxSizing: "border-box",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                  <div style={{ width: 54, height: 54, borderRadius: "50%", backgroundColor: "#000000", display: "flex", alignItems: "center", justifyContent: "center", border: "2px solid #1C1917" }}>
                    <span style={{ fontFamily: "monospace", fontSize: 24, fontWeight: 900, color: "#00F0FF" }}>&#123;</span>
                    <span style={{ fontFamily: "'AntonDisplay', sans-serif", fontSize: 26, fontWeight: 900, color: "#FFFFFF", marginLeft: 1 }}>Z</span>
                  </div>
                  <div>
                    <div style={{ fontFamily: "'BebasDisplay', sans-serif", fontSize: 32, lineHeight: 1.0, color: "#1C1917" }}>
                      ZURA · AI & FUTURE TECH
                    </div>
                    <div style={{ fontFamily: "'RajdhaniDisplay', sans-serif", fontSize: 18, fontWeight: 700, color: "#059669", marginTop: 2 }}>
                      @ZURA.AILAB · AGENT & MOTION
                    </div>
                  </div>
                </div>

                <div
                  style={{
                    backgroundColor: "#EA580C",
                    color: "#FFFFFF",
                    padding: "12px 22px",
                    borderRadius: 999,
                    border: "2px solid #1C1917",
                    boxShadow: "3px 3px 0px #1C1917",
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    fontFamily: "'RajdhaniDisplay', sans-serif",
                    fontSize: 20,
                    fontWeight: 700,
                    letterSpacing: 1.5,
                  }}
                >
                  <span>PANTAU SISTEMNYA</span>
                  <ArrowRight size={20} color="#FFFFFF" />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* DYNAMIC TIMED SUBTITLE (ANCHORED AT BOTTOM: 490px / SAFE ZONE Y: 1430px) */}
      <DynamicSubtitle
        frame={frame}
        subtitles={SUBTITLES}
        bottom={490}
        fontSize={34}
        highlightColor="#F59E0B"
        backgroundColor="rgba(28, 25, 23, 0.94)"
      />
    </AbsoluteFill>
  );
};
